import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to determine the device type (mobile, tablet, desktop, large).
 * @param {Object} breakpoints - Object containing breakpoints for mobile, tablet, desktop, and large screens.
 * @returns {Object} - Returns an object with isMobile, isTablet, isDesktop, and isLarge booleans.
 */
const useDeviceType = (breakpoints = { mobile: 768, tablet: 1024, large: 1280 }) => {
  const getDeviceType = useCallback(() => {
    const width = window.innerWidth;

    if (width < breakpoints.mobile) {
      return 'mobile';
    } else if (width < breakpoints.tablet) {
      return 'tablet';
    } else if (width < breakpoints.large) {
      return 'desktop';
    } else {
      return 'large';
    }
  }, [breakpoints]);

  const [deviceType, setDeviceType] = useState(getDeviceType());

  const handleResize = useCallback(() => {
    setDeviceType(getDeviceType());
  }, [getDeviceType]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    // Initial check on mount
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  // Return the device type status
  return {
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop',
    isDesktopAndAbove: deviceType === 'desktop' || deviceType === 'large',
    isLargeDevice: deviceType === 'large'
  };
};

export default useDeviceType;